import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { Button, tokens } from '@treams/treams-ui';

import TalentReviewActivityRow from './TalentReviewActivityRow';
import TalentReviewSectionTitle from './TalentReviewSectionTitle';

/** Rows shown before "show all activities" is needed. */
const INITIAL_COUNT = 10;

const Empty = styled.p`
	margin: 0;
	color: ${tokens.text.tertiary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	line-height: ${tokens.typography.lineHeights.body};
`;

/**
 * The activity list under the grid, newest first. With a selected person only
 * that person's activities show (moves, joining/leaving, placement help);
 * grid-wide events (submitted, cleared, ...) are hidden in that view.
 * @param props component props
 * @param props.template the grid's template, for cell labels
 * @param props.groupName the group's name, for joining/leaving texts
 * @param props.activity every activity of the grid (or snapshot)
 * @param props.userID the selected person, if any
 */
export default function TalentReviewActivity(props: {
	template: graphql.TalentReviewTemplate;
	groupName: string;
	activity: graphql.TalentReviewActivityFragment[];
	userID?: string;
}) {
	const { t } = useTranslation();
	const [showAll, setShowAll] = useState(false);

	const items = props.userID
		? props.activity.filter((item) => item.user?.id === props.userID)
		: props.activity;
	const visible = showAll ? items : items.slice(0, INITIAL_COUNT);

	return (
		<Box mt={4}>
			<TalentReviewSectionTitle>{t('talentReviews.v2.activity.title')}</TalentReviewSectionTitle>
			<Box mt={3}>
				{items.length === 0 && <Empty>{t('talentReviews.v2.activity.empty')}</Empty>}
				{visible.map((item) => (
					<TalentReviewActivityRow
						key={item.id}
						item={item}
						template={props.template}
						groupName={props.groupName}
					/>
				))}
			</Box>
			{!showAll && items.length > INITIAL_COUNT && (
				<Flex justifyContent="center" mt={3}>
					<Button
						type="secondary"
						title={t('talentReviews.v2.activity.showAll')}
						onClick={() => setShowAll(true)}
					/>
				</Flex>
			)}
		</Box>
	);
}
